import { FormControl, MenuItem, SelectChangeEvent, useMediaQuery, useTheme } from '@mui/material';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useQuotation from '../../../hooks/quotations/useQuotation';
import {
    QuotationAccordionProps,
    QuotationUserProps,
} from '../../../models/quotations/Quotation';
import { CountryNameDisplay } from '../../shared/CountryNameDisplay';
import SelectStyled from '../../shared/SelectStyled';
import TextFieldStyled from '../../shared/TextFieldStyled';
import { QuotationFormItemWrapper } from '../shared/QuotationFormItemWrapper';
import { QuotationFormRowWrapper } from '../shared/QuotationFormRowWrapper';
import { QuotationNextButton } from '../shared/QuotationNextButton';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const schema = yup.object().shape({
    name: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED_CUSTOMER_NAME'),
});

export const QuotationBuyerDetails: React.FC<QuotationAccordionProps> = (
    props
) => {
    const { handleNext, index, quoteData, updateProducts } = props;
    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.FIELDS.QUOTATION_CUSTOMER',
    });
    const theme = useTheme();
    const small = useMediaQuery(theme.breakpoints.down('md'));
    const quotation = useQuotation();
    const {
        register,
        handleSubmit,
        formState: { dirtyFields, isValid, errors },
    } = useForm<QuotationUserProps>({
        defaultValues: quotation && quoteData.buyer ? quoteData.buyer : null,
        resolver: yupResolver(schema),
    });
    const [country, setCountry] = useState('HU');
    const handleCountryChange = (event: SelectChangeEvent) => {
        setCountry(event.target.value as string);
    };

    const onSubmit: SubmitHandler<QuotationUserProps> = (data) => {
        if (isValid) {
            const fieldsToSend = quotation.getModifiedFields(dirtyFields, data);
            if (Object.keys(fieldsToSend).length > 0) {
                quotation
                    .saveBuyer(
                        { ...fieldsToSend, country: country },
                        quoteData.id
                    )
                    .then((response) => {
                        updateProducts();
                        handleNext(index, response);
                    });
            } else {
                handleNext(index);
            }
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <QuotationFormRowWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('NAME')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('name')}
                                error={!!errors.name}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('PHONE')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('phone')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
            </QuotationFormRowWrapper>
            <QuotationFormRowWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('COMPANY_NAME')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('company')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('TAX_NUMBER')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('tax_number')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
            </QuotationFormRowWrapper>

            <QuotationFormRowWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth variant="filled">
                        <DemoItem label={t('COUNTRY')}>
                            <SelectStyled
                                {...register('country', {
                                    onChange: (e) => handleCountryChange(e),
                                })}
                                value={country}
                                sx={{
                                    '& .MuiFilledInput-input': {
                                        padding:
                                            '10px 10px 11px 10px !important',
                                    },
                                }}
                            >
                                <MenuItem value={'HU'}>
                                    <CountryNameDisplay countryCode={'HU'} />
                                </MenuItem>
                            </SelectStyled>
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('POSTCODE')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('postcode')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('CITY')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('city')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
            </QuotationFormRowWrapper>
            <QuotationFormRowWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('ADDRESS1')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('address1')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('ADDRESS2')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('address2')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
            </QuotationFormRowWrapper>
            <QuotationFormRowWrapper>
                <QuotationFormItemWrapper>
                    <FormControl
                        sx={(theme) => ({
                            width: 'calc(50% - 10px)',
                            [theme.breakpoints.down('sm')]: {
                                width: '100%',
                            },
                        })}
                    >
                        <DemoItem label={t('PROJECT_ID')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('project_name')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
            </QuotationFormRowWrapper>
            <QuotationNextButton fullWidth={small} />
        </form>
    );
};
